import { ExpandLess, ExpandMore } from "@mui/icons-material"
import { styled, Typography } from "@mui/material"
import { AssetType } from "@phc/common/gen/preferencesGen"
import { useState } from "react"
import { enumKeys } from "../../../../utils/helpers"
import { extraColors } from "../../../../utils/theme"
import { assetCSVSchema } from "./assetSchema"

const DetailsContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  background: extraColors.dark5,
  borderRadius: 6,
  marginTop: 18,
})

const DetailsHeader = styled("div")({
  display: "flex",
  flexDirection: "row",
  width: "100%",
  justifyContent: "space-between",
  background: extraColors.dark10,
  color: extraColors.white,
  padding: "4px 4px 4px 8px",
  borderRadius: 6,
  alignItems: "center",
})

const SchemaColumnDetailsContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  padding: "0px 12px 12px 12px",
}))

const ColumnValuesContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 2,
})

export const CSVSchemaDetails: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  return (
    <DetailsContainer>
      <DetailsHeader
        onClick={() => setIsExpanded(prev => !prev)}
        sx={{ cursor: "pointer" }}
      >
        <Typography variant="body1Bold" sx={{ color: extraColors.white }}>
          Template Columns
        </Typography>
        {isExpanded ? <ExpandLess /> : <ExpandMore />}
      </DetailsHeader>
      {isExpanded && <SchemaColumnDetails />}
    </DetailsContainer>
  )
}

const SchemaColumnDetails = () => {
  return (
    <SchemaColumnDetailsContainer>
      <Typography
        variant="body1Bold"
        sx={{ margin: "12px 0 4px", color: extraColors.white }}
      >
        Column Names
      </Typography>
      <AssetRowColumnNames />
      <Typography variant="body1Bold" sx={{ margin: "12px 0 4px" }}>
        Column Values
      </Typography>
      <AssetRowColumnValues />
    </SchemaColumnDetailsContainer>
  )
}

const AssetRowColumnNames = () => {
  return (
    <Typography
      variant="body2"
      style={{
        fontFamily: "monospace",
        background: extraColors.dark20,
        padding: "1px 8px",
        marginBottom: "12px",
      }}
    >
      {Object.keys(assetCSVSchema.shape).join(", ")}
    </Typography>
  )
}

const AssetRowColumnValues = () => {
  const rowShape = assetCSVSchema.shape

  return (
    <ColumnValuesContainer>
      <ColumnTitleDesc
        title={rowShape.Name.description}
        desc="Unique name for the location"
      />
      <AssetTypeList />
      <ColumnTitleDesc
        title={rowShape.Address.description}
        desc="Full address"
      />
      <ColumnTitleDesc
        title={rowShape.Latitude.description}
        desc="Number between -90 and 90"
      />
      <ColumnTitleDesc
        title={rowShape.Longitude.description}
        desc="Number between -180 and 180"
      />
      <ColumnTitleDesc
        title={rowShape["Threat Radius"].description}
        desc="Number (in miles)"
      />
      <ColumnTitleDesc
        title="Groups"
        desc="Comma separated list of existing asset group names"
      />
    </ColumnValuesContainer>
  )
}

const ColumnTitleDesc: React.FC<{
  title: string | undefined
  desc?: string
}> = ({ title, desc }) => (
  <Typography variant="body2">
    <Typography
      variant="body2Bold"
      sx={{
        display: "inline",
        color: extraColors.status.navySubtle,
      }}
    >
      {title}:
    </Typography>{" "}
    {desc}
  </Typography>
)

const AssetTypeList = () => {
  return (
    <>
      <ColumnTitleDesc title={assetCSVSchema.shape.Type.description} />
      <ul
        style={{
          maxHeight: 95,
          overflow: "auto",
          marginTop: 0,
        }}
      >
        {enumKeys(AssetType)
          .sort()
          .map(type => (
            <Typography
              component="li"
              key={type}
              variant="body2"
              sx={{
                fontFamily: "monospace",
                background: extraColors.dark20,
                width: "fit-content",
                margin: "2px 0",
              }}
            >
              {type}
            </Typography>
          ))}
      </ul>
    </>
  )
}
